import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Highlighter,
  Italic,
  Strikethrough,
  Type,
  Underline,
} from 'lucide-react';
import type { SigningField } from '../types/fields';
import { FONT_SIZE_PRESETS, resolveFieldTextStyle, isTextBasedFieldType, type FontFamilyKey, type TextAlignKey } from '../utils/fontMapping';
import { fieldTextLayoutMode } from '../utils/textLayout';
import { RESET_TEXT_FORMATTING, type TextFormatPatch } from '../utils/textFormatActions';
import { SIGNATURE_FONT_FAMILY_OPTIONS } from '../constants/signing';
import {
  clearFloatingToolbarAnchor,
  getFloatingToolbarAnchor,
} from '../lib/floatingToolbarAnchor';

const SWATCHES = ['#0d0d1a', '#1e40af', '#b91c1c', '#15803d', '#7c3aed', '#0f766e'];
const HIGHLIGHT_SWATCHES = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fecaca', '#e9d5ff'];

interface DraftNumberInputProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  /** parseInt for integers (font size), parseFloat for decimals (line/letter). */
  integer?: boolean;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  onCommit: (n: number) => void;
}

/**
 * Numeric <input> that keeps a local string draft while focused so the user can
 * clear the box and type intermediate values without per-keystroke clamping.
 * It commits live only when the parsed value is already in range, and otherwise
 * clamps + commits on blur or Enter, then drops the draft to snap back to the
 * canonical controlled value.
 */
const DraftNumberInput: React.FC<DraftNumberInputProps> = ({
  value,
  min,
  max,
  step,
  integer = false,
  disabled,
  className,
  ariaLabel,
  onCommit,
}) => {
  const [draft, setDraft] = useState<string | null>(null);

  const clamp = useCallback(
    (n: number) => Math.max(min, Math.min(max, n)),
    [min, max]
  );

  const commit = useCallback(
    (raw: string) => {
      const n = integer ? parseInt(raw, 10) : parseFloat(raw);
      if (Number.isFinite(n)) {
        const clamped = clamp(n);
        if (clamped !== value) onCommit(clamped);
      }
      // Drop the draft so the input snaps back to the canonical controlled value
      // (this also restores the previous value when the field was left empty/invalid).
      setDraft(null);
    },
    [integer, clamp, value, onCommit]
  );

  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      className={className}
      disabled={disabled}
      aria-label={ariaLabel}
      // While editing, show the raw draft so empty/partial/out-of-range text is allowed.
      value={draft ?? String(value)}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        // Commit live only when the parsed value is already within range, so the
        // canvas updates as the user types without clamping intermediate keystrokes.
        const n = integer ? parseInt(raw, 10) : parseFloat(raw);
        if (Number.isFinite(n) && n >= min && n <= max && n !== value) {
          onCommit(n);
        }
      }}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit((e.target as HTMLInputElement).value);
        }
      }}
    />
  );
};

export interface TextFormatToolbarProps {
  fields: SigningField[];
  targetIds: string[];
  onApply: (patch: TextFormatPatch) => void;
  variant?: 'floating' | 'inspector';
  disabled?: boolean;
}

function primaryField(fields: SigningField[], targetIds: string[]): SigningField | null {
  return fields.find((f) => f.id === targetIds[0]) ?? null;
}

export const TextFormatToolbar: React.FC<TextFormatToolbarProps> = ({
  fields,
  targetIds,
  onApply,
  variant = 'inspector',
  disabled = false,
}) => {
  const field = primaryField(fields, targetIds);
  const labelId = useId();
  const colorInputRef = useRef<HTMLInputElement>(null);
  const highlightInputRef = useRef<HTMLInputElement>(null);

  const patchWithCompact = useCallback(
    (patch: TextFormatPatch) => onApply(patch),
    [onApply]
  );

  if (!field || targetIds.length === 0) return null;

  // Only render the font selector for text-capable field types (not checkbox / image).
  const showFontSelector = isTextBasedFieldType(field.type);

  const layoutMode = fieldTextLayoutMode(field.type, {
    boxHeight: field.height,
    value: field.value,
  });
  const style = resolveFieldTextStyle(field, layoutMode);

  const btnClass = (active?: boolean) =>
    `text-format-btn ${active ? 'active' : ''}`;

  const isFloating = variant === 'floating';

  const allowWorkspaceShortcut = (e: React.KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return false;
    const key = e.key.toLowerCase();
    return key === 'z' || key === 'y';
  };

  return (
    <div
      className={`text-format-toolbar ${isFloating ? 'text-format-toolbar-floating' : 'text-format-toolbar-inspector'}`}
      role="toolbar"
      aria-labelledby={labelId}
      onKeyDown={(e) => {
        // Undo/redo must reach SigningWorkspace's window listener even when a
        // format control (e.g. Bold) still has focus after click.
        if (!allowWorkspaceShortcut(e)) e.stopPropagation();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <span id={labelId} className="sr-only">
        Text formatting{targetIds.length > 1 ? ` (${targetIds.length} fields)` : ''}
      </span>

      {showFontSelector && (
        <select
          className="text-format-select"
          disabled={disabled}
          value={style.fontFamily}
          aria-label="Font family"
          onChange={(e) => onApply({ fontFamily: e.target.value as FontFamilyKey })}
        >
          <optgroup label="Standard">
            <option value="sans">Sans</option>
            <option value="serif">Serif</option>
            <option value="mono">Mono</option>
          </optgroup>
          <optgroup label="Signature">
            {SIGNATURE_FONT_FAMILY_OPTIONS.map(({ key, label }) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </optgroup>
        </select>
      )}

      <div className="text-format-size-group">
        <DraftNumberInput
          value={style.fontSize}
          min={8}
          max={72}
          integer
          className="text-format-size-input"
          disabled={disabled}
          ariaLabel="Font size in points"
          onCommit={(n) => patchWithCompact({ fontSize: n })}
        />
        <span className="text-format-size-unit">pt</span>
      </div>

      {!isFloating && (
        <div className="text-format-presets" role="group" aria-label="Font size presets">
          {FONT_SIZE_PRESETS.map((pt) => (
            <button
              key={pt}
              type="button"
              disabled={disabled}
              className={`text-format-preset ${style.fontSize === pt ? 'active' : ''}`}
              onClick={() => patchWithCompact({ fontSize: pt })}
            >
              {pt}
            </button>
          ))}
        </div>
      )}

      <div className="text-format-divider" aria-hidden />

      <button
        type="button"
        disabled={disabled}
        className={btnClass(style.bold)}
        aria-label="Bold"
        aria-pressed={style.bold}
        title="Bold (Ctrl+B)"
        onClick={() => onApply({ bold: !style.bold })}
      >
        <Bold size={16} strokeWidth={2} />
      </button>
      <button
        type="button"
        disabled={disabled}
        className={btnClass(style.italic)}
        aria-label="Italic"
        aria-pressed={style.italic}
        title="Italic (Ctrl+I)"
        onClick={() => onApply({ italic: !style.italic })}
      >
        <Italic size={16} strokeWidth={2} />
      </button>
      <button
        type="button"
        disabled={disabled}
        className={btnClass(style.underline)}
        aria-label="Underline"
        aria-pressed={style.underline}
        title="Underline (Ctrl+U)"
        onClick={() => onApply({ underline: !style.underline })}
      >
        <Underline size={16} strokeWidth={2} />
      </button>
      <button
        type="button"
        disabled={disabled}
        className={btnClass(style.strikethrough)}
        aria-label="Strikethrough"
        aria-pressed={style.strikethrough}
        title="Strikethrough"
        onClick={() => onApply({ strikethrough: !style.strikethrough })}
      >
        <Strikethrough size={16} strokeWidth={2} />
      </button>

      <div className="text-format-divider" aria-hidden />

      <div className="text-format-align" role="group" aria-label="Text alignment">
        {(
          [
            ['left', AlignLeft],
            ['center', AlignCenter],
            ['right', AlignRight],
          ] as const
        ).map(([align, Icon]) => (
          <button
            key={align}
            type="button"
            disabled={disabled}
            className={btnClass(style.align === align)}
            aria-label={`Align ${align}`}
            aria-pressed={style.align === align}
            onClick={() => onApply({ align: align as TextAlignKey })}
          >
            <Icon size={16} strokeWidth={1.75} />
          </button>
        ))}
      </div>

      <div className="text-format-divider" aria-hidden />

      <div className="text-format-colors" role="group" aria-label="Text color">
        {SWATCHES.map((hex) => (
          <button
            key={hex}
            type="button"
            disabled={disabled}
            className={`text-format-swatch ${style.color === hex ? 'active' : ''}`}
            style={{ backgroundColor: hex }}
            aria-label={`Text color ${hex}`}
            onClick={() => onApply({ color: hex })}
          />
        ))}
        <button
          type="button"
          disabled={disabled}
          className="text-format-btn"
          aria-label="Custom text color"
          title="Custom color"
          onClick={() => colorInputRef.current?.click()}
        >
          <Type size={14} strokeWidth={1.75} />
        </button>
        <input
          ref={colorInputRef}
          type="color"
          className="sr-only"
          value={style.color}
          onChange={(e) => onApply({ color: e.target.value })}
        />
      </div>

      <div className="text-format-colors" role="group" aria-label="Highlight color">
        <button
          type="button"
          disabled={disabled}
          className={`text-format-btn ${!style.highlightColor ? 'active' : ''}`}
          aria-label="No highlight"
          title="No highlight"
          onClick={() => onApply({ highlightColor: undefined })}
        >
          <Highlighter size={14} strokeWidth={1.75} className="opacity-40" />
        </button>
        {HIGHLIGHT_SWATCHES.map((hex) => (
          <button
            key={hex}
            type="button"
            disabled={disabled}
            className={`text-format-swatch ${style.highlightColor === hex ? 'active' : ''}`}
            style={{ backgroundColor: hex }}
            aria-label={`Highlight ${hex}`}
            onClick={() => onApply({ highlightColor: hex })}
          />
        ))}
        {/* Fix A11Y-6: add the missing trigger button for the custom highlight color
             input — the ref existed but had no UI button to open it (unlike text-color). */}
        <button
          type="button"
          disabled={disabled}
          className="text-format-btn"
          aria-label="Custom highlight color"
          title="Custom highlight color"
          onClick={() => highlightInputRef.current?.click()}
        >
          <Highlighter size={14} strokeWidth={1.75} />
        </button>
        <input
          ref={highlightInputRef}
          type="color"
          className="sr-only"
          value={style.highlightColor ?? '#fef08a'}
          onChange={(e) => onApply({ highlightColor: e.target.value })}
        />
      </div>

      {!isFloating && (
        <>
          <div className="text-format-divider" aria-hidden />
          <div className="text-format-spacing">
            <label className="text-format-spacing-label">
              Line
              <DraftNumberInput
                value={style.lineHeight}
                min={0.8}
                max={3}
                step={0.1}
                disabled={disabled}
                onCommit={(v) => onApply({ lineHeight: v })}
              />
            </label>
            <label className="text-format-spacing-label">
              Letter
              <DraftNumberInput
                value={style.letterSpacing}
                min={-2}
                max={24}
                step={0.5}
                disabled={disabled}
                onCommit={(v) => onApply({ letterSpacing: v })}
              />
            </label>
          </div>
          <button
            type="button"
            disabled={disabled}
            className="text-format-reset"
            onClick={() => onApply(RESET_TEXT_FORMATTING)}
          >
            Reset formatting
          </button>
        </>
      )}
    </div>
  );
};

interface FloatingTextFormatToolbarProps {
  field: SigningField;
  screenRect: { left: number; top: number; width: number; height: number };
  fields: SigningField[];
  bulkIds: string[];
  onApply: (patch: TextFormatPatch) => void;
  disabled?: boolean;
}

const TOOLBAR_GAP = 10;
const VIEWPORT_MARGIN = 8;

function computeFloatingToolbarPosition(
  fieldRect: { left: number; top: number; width: number; height: number },
  toolbarSize: { width: number; height: number }
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const clampLeft = (left: number) =>
    Math.max(VIEWPORT_MARGIN, Math.min(left, vw - toolbarSize.width - VIEWPORT_MARGIN));

  const overlapsField = (top: number) => {
    const toolbarBottom = top + toolbarSize.height;
    const fieldBottom = fieldRect.top + fieldRect.height;
    return top < fieldBottom + TOOLBAR_GAP && toolbarBottom > fieldRect.top - TOOLBAR_GAP;
  };

  const aboveTop = fieldRect.top - toolbarSize.height - TOOLBAR_GAP;
  const belowTop = fieldRect.top + fieldRect.height + TOOLBAR_GAP;

  let top: number;
  if (aboveTop >= VIEWPORT_MARGIN && !overlapsField(aboveTop)) {
    top = aboveTop;
  } else if (belowTop + toolbarSize.height <= vh - VIEWPORT_MARGIN && !overlapsField(belowTop)) {
    top = belowTop;
  } else if (belowTop + toolbarSize.height <= vh - VIEWPORT_MARGIN) {
    top = belowTop;
  } else if (aboveTop >= VIEWPORT_MARGIN) {
    top = aboveTop;
  } else {
    top = VIEWPORT_MARGIN;
  }

  const centeredLeft = fieldRect.left + fieldRect.width / 2 - toolbarSize.width / 2;
  return { top, left: clampLeft(centeredLeft) };
}

/** Contextual mini-toolbar positioned near the selected field on the canvas. */
export const FloatingTextFormatToolbar: React.FC<FloatingTextFormatToolbarProps> = ({
  field,
  screenRect,
  fields,
  bulkIds,
  onApply,
  disabled,
}) => {
  // Manage our own anchor to avoid races with the shared singleton + portal cleanups
  // during rapid field rect changes (e.g. live value edit in inspector for text) or delete.
  const [anchor] = useState(() => {
    const el = document.createElement('div');
    el.className = 'text-format-floating-anchor';
    el.dataset.floatingToolbarAnchor = '';
    el.style.position = 'fixed';
    el.style.zIndex = '200';
    el.style.pointerEvents = 'none';
    document.body.appendChild(el);
    return el;
  });

  const [position, setPosition] = useState(() =>
    computeFloatingToolbarPosition(screenRect, { width: 420, height: 40 })
  );

  useLayoutEffect(() => {
    anchor.style.top = `${position.top}px`;
    anchor.style.left = `${position.left}px`;
  }, [anchor, position]);

  useLayoutEffect(() => {
    const measure = () => {
      const toolbarEl = anchor.firstElementChild as HTMLElement | null;
      const toolbarSize = toolbarEl
        ? { width: toolbarEl.offsetWidth, height: toolbarEl.offsetHeight }
        : { width: 420, height: 40 };
      setPosition(computeFloatingToolbarPosition(screenRect, toolbarSize));
    };

    measure();

    const toolbarEl = anchor.firstElementChild;
    if (!toolbarEl || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(toolbarEl);
    return () => observer.disconnect();
  }, [anchor, screenRect]);

  useLayoutEffect(() => {
    // cleanup: remove our anchor when this toolbar instance unmounts
    return () => {
      if (anchor.parentNode) anchor.parentNode.removeChild(anchor);
    };
  }, [anchor]);

  return createPortal(
    <TextFormatToolbar
      fields={fields}
      targetIds={bulkIds.length > 0 ? bulkIds : [field.id]}
      onApply={onApply}
      variant="floating"
      disabled={disabled}
    />,
    anchor,
  );
};